import React, { useState, useEffect, useMemo } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Layout, Dropdown, Select, Progress, Tag, Tooltip } from 'antd';
import { theme, Text, TableComponent, ButtonAnt, Menu, InputComponent, Modal } from '@pfmam/pfmam';
import ThirdPartyHoldingsDetailsModal from './components/thirdPartyHoldingsDetailsModal';
import ThirdPartyHoldingsReviewModal from './components/thirdPartyHoldingsReviewModal';
import ThirdPartyHoldingsAddModal from './components/thirdPartyHoldingsAddModal';
import { PlusCircleOutlined, EllipsisOutlined, WarningOutlined, CheckCircleOutlined, HistoryOutlined, DownloadOutlined, TeamOutlined, DollarOutlined, ClockCircleFilled, InfoCircleOutlined } from '@ant-design/icons';
import { Container, Box, Banner } from '../styles/globalStyle';
import AppLayout from '../common/layouts/appLayout';
import NoDataFound from '../common/components/noDataFound';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { GetThirdPartyHoldings, UpdateThirdPartyHolding, InsertThirdPartyHoldingsAudit, GetThirdPartyHoldingsAudit, RemoveThirdPartyHolding, InsertThirdPartyHolding, GetThirdPartyHoldingsOutstanding, GetThirdPartySecurityDescriptions, GetThirdPartyPortfolios, GetThirdPartyHoldingsRatings, GetThirdPartyHoldingsAccess, GetThirdPartySecurityTypes } from '../actions/actions';

const { Content } = Layout;
const { Option } = Select;

const MONTHS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: String(i + 1).padStart(2, '0'),
}));
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => currentYear - 5 + i);

const DropdownStyle = createGlobalStyle`
  .third-party-holdings-dropdown {
    .ant-dropdown-menu {
      box-shadow: 0px 9px 28px 8px rgba(0, 0, 0, 0.05),
        0px 6px 16px rgba(0, 0, 0, 0.08);
      border-radius: 2px;
      padding: 4px 0;
      .ant-dropdown-menu-item {
        font-size: ${theme.font_size.size_l};
        line-height: 20px;
        color: ${theme.colors.black_secondary};
        padding: 10px 16px;
        &:hover {
          background: ${theme.colors.light_grey};
        }
        &.remove-item {
          color: ${theme.colors.danger_red};
        }
      }
    }
  }
`;

const ContentWrapper = styled(Content)`
  .ant-table {
    border-radius: 4px;
    .ant-table-tbody {
      & > tr {
        cursor: pointer;
        &:hover {
          &.ant-table-row:not(.ant-table-expanded-row) {
            background: linear-gradient(
                0deg,
                ${(props) => props.rgbaHover},
                ${(props) => props.rgbaHover}
              ),
              ${theme.colors.white};
          }
        }
        & > td {
          vertical-align: top;
          padding: 20px 32px;
          &.ant-table-column-sort {
            background: ${theme.colors.transparent};
          }
        }
        &:last-child {
          & > td {
            border-bottom-color: ${theme.colors.transparent};
          }
        }
        &.inactive-row > td {
          color: ${theme.colors.grey_tertiary};
        }
      }
    }
    .ant-table-thead {
      & > tr {
        & > th {
          background: ${theme.colors.light_grey};
          font-family: 'Inter';
          padding: 0 32px;
          font-weight: normal;
          line-height: 20px;
          &:not(:last-child) {
            &:not(.ant-table-selection-column) {
              &:not(.ant-table-row-expand-icon-cell) {
                &:not([colspan]) {
                  &::before {
                    background-color: ${theme.colors.transparent};
                  }
                }
              }
            }
          }
        }
      }
    }
    &.ant-table.ant-table-middle {
      .ant-table-thead {
        .ant-table-column-sorters {
          padding: 0;
          .ant-table-column-sorter-inner .anticon {
            color: ${theme.colors.grey_stone};
          }
          .ant-table-column-sorter-up.active,
          .ant-table-column-sorter-down.active {
            color: ${theme.colors.black_sorted};
          }
        }
        .ant-table-column-sorter-inner {
          margin-top: 0;
        }
      }
    }
    .sort-space-none {
      .ant-table-column-sorters {
        justify-content: flex-start;
      }
      .ant-table-column-title {
        flex: 0 0 auto;
      }
    }
  }
  .ant-table-title {
    padding: 0 !important;
  }
`;

const ModalTableWrapper = styled.div`
  .ant-table {
    border-radius: 4px;
    .ant-table-thead {
      & > tr {
        & > th {
          background: ${theme.colors.light_grey};
          font-family: 'Inter';
          padding: 0 32px;
          font-weight: normal;
          line-height: 20px;
          &:not(:last-child) {
            &:not(.ant-table-selection-column) {
              &:not(.ant-table-row-expand-icon-cell) {
                &:not([colspan]) {
                  &::before {
                    background-color: ${theme.colors.transparent};
                  }
                }
              }
            }
          }
        }
      }
    }
    &.ant-table.ant-table-middle {
      .ant-table-thead {
        .ant-table-column-sorters {
          padding: 0;
          .ant-table-column-sorter-inner .anticon {
            color: ${theme.colors.grey_stone};
          }
          .ant-table-column-sorter-up.active,
          .ant-table-column-sorter-down.active {
            color: ${theme.colors.black_sorted};
          }
        }
        .ant-table-column-sorter-inner {
          margin-top: 0;
        }
      }
    }
    .sort-space-none {
      .ant-table-column-sorters {
        justify-content: flex-start;
      }
      .ant-table-column-title {
        flex: 0 0 auto;
      }
    }
  }
`;

const ActionButton = styled.button`
  height: 32px;
  width: 32px;
  background: ${theme.colors.transparent};
  border: 0;
  padding: 0;
  color: ${theme.colors.black_secondary};
  box-shadow: none;
  cursor: pointer;
  &:focus,
  &:hover {
    background: ${theme.colors.transparent};
    color: ${theme.colors.black_secondary};
  }
  &.ant-dropdown-open {
    background: ${theme.colors.light_grey};
  }
`;

const Wrapper = styled.div`
  padding-top: 40px;
`;

const PeriodBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.grey_fossil};
  border-radius: 8px;
  margin-bottom: 24px;
`;

const PeriodLabel = styled.span`
  font-size: ${theme.font_size.size_xl};
  font-weight: 600;
  color: ${theme.colors.black_secondary};
  font-family: 'Inter', sans-serif;
  margin-right: 4px;
`;

const StatStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.grey_fossil};
  border-radius: 8px;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${(props) => props.color || theme.colors.clean_black};
  font-family: 'Inter', sans-serif;
  line-height: 1.2;
`;

const StatSubLabel = styled.div`
  font-size: ${theme.font_size.size_l};
  color: ${theme.colors.grey_tertiary};
  font-family: 'Inter', sans-serif;
  line-height: 18px;
`;

const HoverButton = styled(ButtonAnt)`
  &&& {
    background: ${(props) => props.linkTextColor};
    color: white;
    transition: box-shadow 0.2s ease, transform 0.1s ease;
    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
      transform: translateY(-1px);
      filter: brightness(0.93);
    }
    &:active {
      transform: translateY(0);
      box-shadow: none;
      filter: brightness(0.85);
    }
  }
`;

const TabToggleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${theme.colors.light_grey};
  border-radius: 8px;
  padding: 3px;
`;

const TabToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-size: ${theme.font_size.size_l};
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  line-height: 20px;
  transition: background 0.15s ease, color 0.15s ease;
  background: ${(props) => (props.active ? theme.colors.white : 'transparent')};
  color: ${(props) => (props.active ? theme.colors.clean_black : theme.colors.grey_tertiary)};
  box-shadow: ${(props) => (props.active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none')};
  &:hover {
    color: ${theme.colors.clean_black};
    background: ${(props) => (props.active ? theme.colors.white : 'rgba(0,0,0,0.05)')};
  }
`;

const sampleData = [
  {
    key: '1',
    linkedAccount: '99975981500',
    balance: '$35,352,879.82',
    monthEnd: 'Jan 2024',
    securityDescription: 'US Bank',
    lastModified: '1/5/2024',
    isActive: true,
  },
  {
    key: '2',
    linkedAccount: '',
    balance: '$32,567,5552.47',
    monthEnd: 'Dec 2023',
    securityDescription: 'US Bank',
    lastModified: '',
    isActive: false,
  },
  {
    key: '3',
    linkedAccount: '99976950000',
    balance: '$10,038,550.33',
    monthEnd: 'Jan 2024',
    securityDescription: 'Bank of America',
    lastModified: '1/5/2024',
    isActive: true,
  },
  {
    key: '4',
    linkedAccount: '99977100001',
    balance: '$8,214,300.00',
    monthEnd: 'Jan 2024',
    securityDescription: 'Wells Fargo',
    lastModified: '1/8/2024',
    isActive: true,
  },
  {
    key: '5',
    linkedAccount: '99977200002',
    balance: '$12,500,000.00',
    monthEnd: 'Jan 2024',
    securityDescription: 'JPMorgan Chase',
    lastModified: '1/10/2024',
    isActive: true,
  },
  {
    key: '6',
    linkedAccount: '99977300003',
    balance: '$4,750,800.45',
    monthEnd: 'Jan 2024',
    securityDescription: 'Citibank',
    lastModified: '1/12/2024',
    isActive: true,
  },
  {
    key: '7',
    linkedAccount: '99977400004',
    balance: '$21,000,000.00',
    monthEnd: 'Jan 2024',
    securityDescription: 'PNC Bank',
    lastModified: '1/15/2024',
    isActive: true,
  },
  {
    key: '8',
    linkedAccount: '99977500005',
    balance: '$6,312,400.20',
    monthEnd: 'Feb 2024',
    securityDescription: 'TD Bank',
    lastModified: '2/1/2024',
    isActive: true,
  },
  {
    key: '9',
    linkedAccount: '99977600006',
    balance: '$9,875,100.75',
    monthEnd: 'Feb 2024',
    securityDescription: 'Truist Bank',
    lastModified: '2/3/2024',
    isActive: true,
  },
  {
    key: '10',
    linkedAccount: '99977700007',
    balance: '$3,401,200.00',
    monthEnd: 'Feb 2024',
    securityDescription: 'Capital One',
    lastModified: '2/5/2024',
    isActive: true,
  },
  {
    key: '11',
    linkedAccount: '99977800008',
    balance: '$17,650,000.00',
    monthEnd: 'Feb 2024',
    securityDescription: 'US Bank',
    lastModified: '2/7/2024',
    isActive: true,
  },
  {
    key: '12',
    linkedAccount: '99977900009',
    balance: '$5,100,900.60',
    monthEnd: 'Feb 2024',
    securityDescription: 'Bank of America',
    lastModified: '2/9/2024',
    isActive: true,
  },
  {
    key: '13',
    linkedAccount: '99978000010',
    balance: '$14,225,750.33',
    monthEnd: 'Feb 2024',
    securityDescription: 'Wells Fargo',
    lastModified: '2/11/2024',
    isActive: true,
  },
];

const formatYearMonth = (yearMonth) => {
  const str = yearMonth.toString();
  const year = str.slice(0, 4);
  const month = parseInt(str.slice(4, 6), 10);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[month - 1]} ${year}`;
};

const formatCurrency = (value) =>
  value != null
    ? `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '';

const formatDateMMDDYYYY = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const MAX_ACCRUED_INTEREST_VALUE = 99999999.99;
const MAX_EFFECTIVE_DURATION_VALUE = 999.9999;
const MAX_MONTHLY_BALANCE_VALUE = 9999999999999999.99;

const parseNumericOptional = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const num = parseFloat(String(value).replace(/[$,]/g, ''));
  return isNaN(num) ? null : num;
};

const formatNumericDisplay = (value, maxDecimals = 2) => {
  if (value === undefined || value === null || value === '') return '';
  const num = parseFloat(String(value).replace(/,/g, ''));
  if (isNaN(num)) return String(value);
  return num.toLocaleString('en-US', { maximumFractionDigits: maxDecimals });
};

const mapHoldings = (data) =>
  data.map((item, index) => ({
    key: `${item.AccountUniqueID}-${item.YearMonth}-${index}`,
    linkedAccount: item.AccountUniqueID || '',
    balance: formatCurrency(item.Balance),
    monthEnd: item.YearMonth ? formatYearMonth(item.YearMonth) : '',
    yearMonth: item.YearMonth || null,
    securityDescription: item.SecurityDescription || '',
    lastModified: item.lastModifiedDate
      ? new Date(item.lastModifiedDate).toLocaleDateString('en-US')
      : '',
    isActive: true,
  }));

const mapDetailsToForm = (item) => ({
  'Security Description': item.SecurityDescription || '',
  'Linked PFMAM Account': item.AccountUniqueID || '',
  'Monthly Balance': formatCurrency(item.Balance),
  'CUSIP': '',
  'Issuer Ticker': item.Issuer || '',
  'Accrued Interest': formatCurrency(item.AccruedInterest),
  'YTM at Cost': formatNumericDisplay(item.YTMCost ?? item.CurrentYield, 2),
  'YTM Market': formatNumericDisplay(item.YTMMarket, 2),
  'SEC Type': item.SECType || item.SecType || item.SecurityType || '',
  'Maturity Date': formatDateMMDDYYYY(item.MaturityDate || item.Maturity || item.Maturity_Date),
  'S&P Rating': item.StandardAndPoors || '',
  'Moody Rating': item.Moodys || '',
  'Fitch Rating': item.Fitch || '',
  'WAM': item.DTMWAM || '',
  'Effective Duration': formatNumericDisplay(item.Duration, 4),
  'Last Updated': item.lastModifiedDate
    ? new Date(item.lastModifiedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '',
  'Include In Monthly Statement': item.MonthlyStatement === 1 || item.MonthlyStatement === true ? 'Yes' : 'No',
});

const ThirdPartyHoldingsContainer = () => {
  const primary_color = useSelector((state) => state.siteInfo.primaryColor);
  const rgbaPrimary = useSelector((state) => state.siteInfo.rgbaPrimary);
  const clientDetails = useSelector((state) => state.programsClientsSlice.selectedClient);
  const ssnorTIN = String(clientDetails?.payload?.[0]?.ssnortin || '').trim();
  const webuserAccessType = useSelector((state) => state.userSlice.webUserType);
  const history = useHistory();
  const [thirdPartyHoldings, setThirdPartyHoldings] = useState([]);
  const [loadingHoldings, setLoadingHoldings] = useState(false);
  const [pagination, setPagination] = useState({
    position: ['bottomRight'],
    showQuickJumper: true,
    size: 'default',
    current: 1,
  });
  const [searchText, setSearchText] = useState('');
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [isReviewModalVisible, setIsReviewModalVisible] = useState(false);
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false);
  const [recordToRemove, setRecordToRemove] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [originalValues, setOriginalValues] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [submittingUpdate, setSubmittingUpdate] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [detailsFormErrors, setDetailsFormErrors] = useState({});
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [auditSearchText, setAuditSearchText] = useState('');
  const [rawHoldings, setRawHoldings] = useState([]);
  const [rawOutstandingHoldings, setRawOutstandingHoldings] = useState([]);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [submittingAdd, setSubmittingAdd] = useState(false);
  const [addSubmitError, setAddSubmitError] = useState(null);
  const [isAddSuccessModalVisible, setIsAddSuccessModalVisible] = useState(false);
  const [securityDescriptionOptions, setSecurityDescriptionOptions] = useState([]);
  const [securityTypeOptions, setSecurityTypeOptions] = useState([]);
  const [portfolioOptions, setPortfolioOptions] = useState([]);
  const [ratingOptions, setRatingOptions] = useState({
    sp: [],
    moody: [],
    fitch: [],
  });
  const [activeTab, setActiveTab] = useState('submitted');
  const [outstandingData, setOutstandingData] = useState([]);
  const [loadingOutstanding, setLoadingOutstanding] = useState(false);
  const [isDuplicateModalVisible, setIsDuplicateModalVisible] = useState(false);
  const [canShowAddNewButton, setCanShowAddNewButton] = useState(false);

  useEffect(() => {
    let mounted = true;

    if (webuserAccessType === 0) {
      setCanShowAddNewButton(true);
      return () => {
        mounted = false;
      };
    }

    if (webuserAccessType !== 1 && webuserAccessType !== 2) {
      setCanShowAddNewButton(false);
      return () => {
        mounted = false;
      };
    }

    GetThirdPartyHoldingsAccess()
      .then((response) => {
        if (!mounted) return;
        const data = response?.data;
        const accessValue =
          typeof data === 'boolean'
            ? data
            : data?.HasAccess ?? data?.hasAccess ?? data?.Access ?? data?.access ?? false;
        setCanShowAddNewButton(accessValue === true || accessValue === 'TRUE' || accessValue === 1);
      })
      .catch(() => {
        if (!mounted) return;
        setCanShowAddNewButton(false);
      });

    return () => {
      mounted = false;
    };
  }, [webuserAccessType]);

  const fetchRatingsOptions = () => {
    return GetThirdPartyHoldingsRatings()
      .then((response) => {
        const raw = response.data;
        const records = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.Table)
          ? raw.Table
          : Array.isArray(raw?.value)
          ? raw.value
          : Array.isArray(raw?.data)
          ? raw.data
          : [];

        const unique = (arr) => Array.from(new Set(arr.filter(Boolean)));
        const normaliseCompany = (value) =>
          String(value || '')
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '');

        const sp = [];
        const moody = [];
        const fitch = [];

        records.forEach((item) => {
          const company = normaliseCompany(
            item?.RatingCompany ?? item?.ratingCompany
          );
          const rating = String(item?.Rating ?? item?.rating ?? '').trim();
          if (!rating) return;

          if (company === 's&p' || company === 'sp') sp.push(rating);
          if (company === 'moody') moody.push(rating);
          if (company === 'fitch') fitch.push(rating);
        });

        setRatingOptions({
          sp: unique(sp),
          moody: unique(moody),
          fitch: unique(fitch),
        });
      })
      .catch((error) => {
        console.log('[ThirdPartyHoldings] ratings options error:', error);
        setRatingOptions({ sp: [], moody: [], fitch: [] });
      });
  };

  const fetchSecurityTypeOptions = () => {
    return GetThirdPartySecurityTypes()
      .then((response) => {
        const raw = response.data;
        const records = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.Table)
          ? raw.Table
          : Array.isArray(raw?.value)
          ? raw.value
          : Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(raw?.result)
          ? raw.result
          : [];

        const options = Array.from(
          new Set(
            records
              .map((item) => String(item?.SecurityTypeDescLong ?? '').trim())
              .filter(Boolean)
          )
        );

        setSecurityTypeOptions(options);
      })
      .catch((error) => {
        console.log('[ThirdPartyHoldings] security types error:', error);
        setSecurityTypeOptions([]);
      });
  };

  const fetchOutstandingHoldings = () => {
    if (!ssnorTIN) return;
    setLoadingOutstanding(true);
    const ym = parseInt(`${selectedYear}${String(selectedMonth).padStart(2, '0')}`, 10);
    GetThirdPartyHoldingsOutstanding(ssnorTIN, ym)
      .then((response) => {
        const raw = response.data;
        const records = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.Table)
          ? raw.Table
          : Array.isArray(raw?.value)
          ? raw.value
          : Array.isArray(raw?.data)
          ? raw.data
          : [];
        setOutstandingData(
          records.map((item, index) => ({
            key: `outstanding-${index}`,
            securityDescription: item.SecurityDescription || '',
            portfolio: item.AccountUniqueID || '',
          }))
        );
        setRawOutstandingHoldings(records);
      })
      .catch((error) => {
        console.log('[ThirdPartyHoldings] outstanding error:', error);
        setRawOutstandingHoldings([]);
      })
      .finally(() => {
        setLoadingOutstanding(false);
      });
  };

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  const fetchHoldings = () => {
    if (!ssnorTIN) return;
    setLoadingHoldings(true);
    const ym = parseInt(`${selectedYear}${String(selectedMonth).padStart(2, '0')}`, 10);
    GetThirdPartyHoldings(ssnorTIN, null, ym, null)
      .then((response) => {
        const raw = response.data;
        const records = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.Table)
          ? raw.Table
          : Array.isArray(raw?.value)
          ? raw.value
          : Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(raw?.result)
          ? raw.result
          : [];
        setThirdPartyHoldings(mapHoldings(records));
        setRawHoldings(records);
      })
      .catch((error) => {
        console.log('[ThirdPartyHoldings] error:', error);
      })
      .finally(() => {
        setLoadingHoldings(false);
      });
  };

  useEffect(() => {
    fetchHoldings();
    fetchOutstandingHoldings();
  }, [ssnorTIN, selectedMonth, selectedYear]);

  const periodLabel = `${MONTHS.find((m) => m.value === selectedMonth)?.label} / ${selectedYear}`;

  // Holdings for the selected period
  const periodHoldings = useMemo(() => {
    const ym = parseInt(`${selectedYear}${String(selectedMonth).padStart(2, '0')}`, 10);
    return thirdPartyHoldings.filter((r) => r.yearMonth === ym);
  }, [thirdPartyHoldings, selectedMonth, selectedYear]);

  // Holdings NOT in the selected period (outstanding)
  const outstandingHoldings = useMemo(() => {
    const ym = parseInt(`${selectedYear}${String(selectedMonth).padStart(2, '0')}`, 10);
    return thirdPartyHoldings.filter((r) => r.yearMonth !== ym);
  }, [thirdPartyHoldings, selectedMonth, selectedYear]);

  // Active dataset based on tab, then apply search
  const activeHoldings = activeTab === 'submitted' ? periodHoldings : outstandingHoldings;
  const filteredData = searchText
    ? activeHoldings.filter((record) =>
        ['linkedAccount', 'balance', 'monthEnd', 'securityDescription', 'lastModified'].some(
          (field) =>
            record[field] &&
            record[field].toString().toLowerCase().includes(searchText.toLowerCase())
        )
      )
    : activeHoldings;

  // Total balance for the selected period (strip $ and commas before parsing)
  const periodTotalBalance = useMemo(
    () => periodHoldings.reduce((sum, r) => {
      const num = parseFloat(String(r.balance).replace(/[$,]/g, ''));
      return sum + (isNaN(num) ? 0 : num);
    }, 0),
    [periodHoldings]
  );

  // All unique year-months that have been submitted
  const submittedYearMonths = useMemo(
    () => new Set(thirdPartyHoldings.map((r) => r.yearMonth).filter(Boolean)),
    [thirdPartyHoldings]
  );

  // Determine if this period has been submitted
  const selectedYm = parseInt(`${selectedYear}${String(selectedMonth).padStart(2, '0')}`, 10);
  const periodSubmitted = submittedYearMonths.has(selectedYm);
  const totalPeriods = periodHoldings.length;
  const totalTracked = periodHoldings.length + outstandingData.length;
  const completionPct = totalTracked > 0 ? Math.round((totalPeriods / totalTracked) * 100) : 0;

  function handleTableChange(current) {
    setPagination((prev) => ({ ...prev, current }));
  }

  const columns = [
    {
      title: 'Linked Account',
      dataIndex: 'linkedAccount',
      key: 'linkedAccount',
      sorter: (a, b) => a.linkedAccount.localeCompare(b.linkedAccount),
      className: 'sort-space-none',
      render: (text) => (
        <Text
          label={text}
          fontSize={`${theme.font_size.size_xl}`}
          lineheight="20px"
        />
      ),
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      className: 'sort-space-none',
      render: (text, record) => (
        <Text
          label={text}
          fontSize={`${theme.font_size.size_xl}`}
          color={record.isActive ? undefined : `${theme.colors.grey_tertiary}`}
          lineheight="20px"
        />
      ),
    },
    {
      title: 'Month End',
      dataIndex: 'monthEnd',
      key: 'monthEnd',
      sorter: (a, b) => new Date(a.monthEnd) - new Date(b.monthEnd),
      className: 'sort-space-none',
      render: (text, record) => (
        <Text
          label={text}
          fontSize={`${theme.font_size.size_xl}`}
          color={record.isActive ? undefined : `${theme.colors.grey_tertiary}`}
          lineheight="20px"
        />
      ),
    },
    {
      title: 'Security Description',
      dataIndex: 'securityDescription',
      key: 'securityDescription',
      sorter: (a, b) => a.securityDescription.localeCompare(b.securityDescription),
      className: 'sort-space-none',
      render: (text, record) => (
        <Text
          label={text}
          fontSize={`${theme.font_size.size_xl}`}
          color={record.isActive ? undefined : `${theme.colors.grey_tertiary}`}
          lineheight="20px"
        />
      ),
    },
    {
      title: 'Last Modified',
      dataIndex: 'lastModified',
      key: 'lastModified',
      sorter: (a, b) => a.lastModified.localeCompare(b.lastModified),
      className: 'sort-space-none',
      render: (text) => (
        <Text
          label={text}
          fontSize={`${theme.font_size.size_xl}`}
          lineheight="20px"
        />
      ),
    },
    {
      title: 'Action',
      key: 'actions',
      align: 'right',
      width: 100,
      className: 'action-cell sort-space-none',
      render: (_, record) =>
        record.isActive ? (
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            overlayClassName="third-party-holdings-dropdown"
            onClick={(e) => e.stopPropagation()}
            overlay={
              <Menu
                type={'basic'}
                multiple={false}
                mode={'inline'}
                onClick={(e) => {
                  if (e.key === 'details') handleDetails(record);
                  if (e.key === 'remove') handleRemove(record);
                }}
                items={[
                  { title: 'Details', disabled: false, key: 'details' },
                  ...(canShowAddNewButton
                    ? [{ title: 'Remove', disabled: false, key: 'remove', className: 'remove-item' }]
                    : []),
                ]}
                width={'auto'}
              />
            }
          >
            <ActionButton onClick={(e) => e.stopPropagation()}>
              <EllipsisOutlined />
            </ActionButton>
          </Dropdown>
        ) : null,
    },
  ];

  const handleDetails = (record) => {
    fetchSecurityTypeOptions();
    fetchRatingsOptions();
    setSelectedRecord(record);
    setEditingField(null);
    setFormValues({});
    setOriginalValues({});
    setDetailsFormErrors({});
    setIsDetailsModalVisible(true);
    setLoadingDetails(true);
    GetThirdPartyHoldings(ssnorTIN, record.linkedAccount, record.yearMonth, record.securityDescription)
      .then((response) => {
        const raw = response.data;
        const records = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.Table)
          ? raw.Table
          : [];
        const item = records[0];
        if (item) {
          const values = mapDetailsToForm(item);
          setFormValues(values);
          setOriginalValues(values);
        }
      })
      .catch((error) => {
        console.log('[ThirdPartyHoldings] details error:', error);
      })
      .finally(() => {
        setLoadingDetails(false);
      });
  };

  const handleDetailsModalClose = () => {
    setIsDetailsModalVisible(false);
    setIsReviewModalVisible(false);
    setSelectedRecord(null);
    setFormValues({});
    setOriginalValues({});
    setDetailsFormErrors({});
    setEditingField(null);
  };

  const validateDetailsField = (label, value) => {
    if (label === 'Monthly Balance' && value) {
      const monthlyBalance = parseNumericOptional(value);
      if (monthlyBalance === null) {
        return 'Monthly Balance must be a valid number';
      }
      if (monthlyBalance > MAX_MONTHLY_BALANCE_VALUE) {
        return 'Monthly Balance cannot exceed 9,999,999,999,999,999.99';
      }
    }

    if (label === 'Issuer Ticker' && value) {
      if (!/^[a-z0-9 ]+$/i.test(value)) {
        return 'Issuer Ticker must contain only letters, numbers, or spaces';
      }
      if (value.length > 39) {
        return 'Issuer Ticker cannot exceed 39 characters';
      }
    }

    if (label === 'SEC Type' && value && String(value).length > 255) {
      return 'SEC Type cannot exceed 255 characters';
    }

    if (label === 'Accrued Interest' && value) {
      const accruedInterest = parseNumericOptional(value);
      if (accruedInterest === null) {
        return 'Accrued Interest must be a valid number';
      }
      if (accruedInterest > MAX_ACCRUED_INTEREST_VALUE) {
        return 'Accrued Interest cannot exceed 99,999,999.99';
      }
    }

    if (label === 'WAM' && value && String(value).length > 49) {
      return 'WAM cannot exceed 49 characters';
    }

    if (label === 'WAM' && value) {
      const wam = parseNumericOptional(value);
      if (wam === null) {
        return 'WAM must be a valid number';
      }
    }

    if (label === 'Effective Duration' && value) {
      const effectiveDuration = parseNumericOptional(value);
      if (effectiveDuration === null) {
        return 'Effective Duration must be a valid number';
      }
      if (effectiveDuration > MAX_EFFECTIVE_DURATION_VALUE) {
        return 'Effective Duration cannot exceed 999.9999';
      }
    }

    if (label === 'YTM at Cost' && value) {
      const ytmAtCost = parseNumericOptional(value);
      if (ytmAtCost === null) {
        return 'YTM at Cost must be a valid number';
      }
      if (ytmAtCost > 10) {
        return 'YTM at Cost cannot be greater than 10.00%';
      }
    }

    if (label === 'YTM Market' && value) {
      const ytmMarket = parseNumericOptional(value);
      if (ytmMarket === null) {
        return 'YTM Market must be a valid number';
      }
      if (ytmMarket > 10) {
        return 'YTM Market cannot be greater than 10.00%';
      }
    }

    return null;
  };

  const handleFieldChange = (label, value) => {
    setFormValues((prev) => ({ ...prev, [label]: value }));
    const fieldError = validateDetailsField(label, value);
    setDetailsFormErrors((prev) => ({ ...prev, [label]: fieldError || undefined }));
  };

  const getPendingChanges = () =>
    Object.keys(originalValues)
      .filter((label) => formValues[label] !== originalValues[label])
      .map((label) => ({
        label,
        originalValue: originalValues[label],
        newValue: formValues[label],
      }));

  const handleUpdateDetailsClick = () => {
    const validationErrors = {};
    ['Monthly Balance', 'Issuer Ticker', 'SEC Type', 'Accrued Interest', 'YTM at Cost', 'YTM Market', 'WAM', 'Effective Duration'].forEach((label) => {
      const error = validateDetailsField(label, formValues[label]);
      if (error) validationErrors[label] = error;
    });

    if (Object.keys(validationErrors).length > 0) {
      setDetailsFormErrors(validationErrors);
      return;
    }

    const changes = getPendingChanges();
    if (changes.length === 0) return;
    setIsReviewModalVisible(true);
  };

  const handleConfirmSubmit = () => {
    const changes = getPendingChanges();
    if (changes.length === 0) return;

    const updatedFields = changes.reduce((acc, { label, newValue }) => {
      acc[label] = newValue;
      return acc;
    }, {});

    setSubmittingUpdate(true);
    setSubmitError(null);
    UpdateThirdPartyHolding(
      ssnorTIN,
      selectedRecord.linkedAccount,
      selectedRecord.yearMonth,
      updatedFields,
      selectedRecord.securityDescription
    )
      .then(() => {
        const changedColumns = changes
          .map(({ label, originalValue, newValue }) => `${label}: "${originalValue}" to "${newValue}"`)
          .join('; ');
        InsertThirdPartyHoldingsAudit(
          selectedRecord.linkedAccount,
          ssnorTIN,
          `Updated third-party holding fields: ${changedColumns}`
        ).catch((auditError) => {
          console.log('[ThirdPartyHoldings] audit error:', auditError);
        });
        setIsReviewModalVisible(false);
        setIsDetailsModalVisible(false);
        setIsSuccessModalVisible(true);
      })
      .catch((error) => {
        console.log('[ThirdPartyHoldings] update error:', error);
        setSubmitError('Failed to save changes. Please try again.');
      })
      .finally(() => {
        setSubmittingUpdate(false);
      });
  };

  const handleSuccessOk = () => {
    setIsSuccessModalVisible(false);
    setSelectedRecord(null);
    setFormValues({});
    setOriginalValues({});
    setEditingField(null);
    setSubmitError(null);
    fetchHoldings();
  };

  const handleReviewBack = () => {
    setIsReviewModalVisible(false);
  };

  const handleUpdate = (record) => {
    console.log('Update clicked', record);
  };

  const handleRemove = (record) => {
    setRecordToRemove(record);
    setIsRemoveModalVisible(true);
  };

  const handleConfirmRemove = () => {
    if (!recordToRemove) return;
    RemoveThirdPartyHolding(recordToRemove.linkedAccount, recordToRemove.yearMonth, recordToRemove.securityDescription, ssnorTIN)
      .then(() => {
        InsertThirdPartyHoldingsAudit(
          recordToRemove.linkedAccount,
          ssnorTIN,
          `User removed third party holding for month of ${recordToRemove.monthEnd} for account ${recordToRemove.linkedAccount} - Security Description: ${recordToRemove.securityDescription}`
        ).catch((auditError) => {
          console.log('[ThirdPartyHoldings] remove audit error:', auditError);
        });
        setIsRemoveModalVisible(false);
        setRecordToRemove(null);
        fetchHoldings();
      })
      .catch((error) => {
        console.log('[ThirdPartyHoldings] remove error:', error);
      });
  };

  const handleCancelRemove = () => {
    setIsRemoveModalVisible(false);
    setRecordToRemove(null);
  };

  const handleAddNew = () => {
    setAddSubmitError(null);
    setIsAddModalVisible(true);
    fetchSecurityTypeOptions();
    GetThirdPartySecurityDescriptions()
      .then((response) => {
        const raw = response.data;
        const items = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.Table)
          ? raw.Table
          : Array.isArray(raw?.value)
          ? raw.value
          : Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(raw?.result)
          ? raw.result
          : Array.isArray(raw?.Results)
          ? raw.Results
          : [];
        // Normalise field names regardless of casing
        const normalised = items
          .map((item) => {
            if (!item || typeof item !== 'object') return null;
            return {
              SecurityDescription:
                item.SecurityDescription ??
                item.securityDescription ??
                item.Description ??
                item.description ??
                null,
              IncludeInMonthlyStatement:
                item.IncludeInMonthlyStatement ??
                item.includeInMonthlyStatement ??
                null,
              IncludeInQuarterlyStatement:
                item.IncludeInQuarterlyStatement ??
                item.IncludeInQuarterlyPerformanceReport ??
                item.includeInQuarterlyStatement ??
                null,
            };
          })
          .filter((item) => item && item.SecurityDescription);
        setSecurityDescriptionOptions(normalised);
      })
      .catch((error) => {
        console.log('[ThirdPartyHoldings] security descriptions error:', error);
      });
    GetThirdPartyPortfolios(ssnorTIN)
      .then((response) => {
        const raw = response.data;
        const items = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.Table)
          ? raw.Table
          : Array.isArray(raw?.value)
          ? raw.value
          : Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(raw?.result)
          ? raw.result
          : [];
        setPortfolioOptions(items.filter((item) => item && item.PortfolioDescription));
      })
      .catch((error) => {
        console.log('[ThirdPartyHoldings] portfolio options error:', error);
      });
    fetchRatingsOptions();
  };

  const handleAddModalClose = () => {
    setIsAddModalVisible(false);
    setAddSubmitError(null);
  };

  const handleAddSubmit = (payload) => {
    setSubmittingAdd(true);
    setAddSubmitError(null);
    // Map modal payload fields to API-expected field names
    const apiPayload = {
      YearMonth: String(payload.YearMonth),
      SecurityDescription: payload.SecurityDescription,
      Balance: payload.Balance ?? null,
      MonthlyStatement: payload.IncludeInMonthlyStatement ?? null,
      AccountUniqueID: payload.LinkedPfmamAccount || null,
      // Optional additional details
      ...(payload['Issuer Ticker']        ? { Issuer: payload['Issuer Ticker'] }                                          : {}),
      ...(payload['SEC Type']             ? { SECType: payload['SEC Type'] }                                               : {}),
      ...(payload['Accrued Interest']     ? { AccruedInterest: payload['Accrued Interest'].replace(/[$,]/g, '') }       : {}),
      ...(payload['YTM at Cost']          ? { YTMCost: payload['YTM at Cost'].replace(/[$,]/g, '') }                    : {}),
      ...(payload['YTM Market']           ? { YTMMarket: payload['YTM Market'].replace(/[$,]/g, '') }                   : {}),
      ...(payload['Maturity Date']        ? { MaturityDate: payload['Maturity Date'] }                                  : {}),
      ...(payload['S&P Rating']           ? { StandardAndPoors: payload['S&P Rating'] }                                  : {}),
      ...(payload['Fitch Rating']         ? { Fitch: payload['Fitch Rating'] }                                           : {}),
      ...(payload['Moody Rating']         ? { Moodys: payload['Moody Rating'] }                                          : {}),
      ...(payload['WAM']                  ? { DTMWAM: payload['WAM'] }                                                   : {}),
      ...(payload['Effective Duration']   ? { Duration: payload['Effective Duration'].replace(/[$,]/g, '') }             : {}),
    };
    InsertThirdPartyHolding(ssnorTIN, apiPayload)
      .then(() => {
        InsertThirdPartyHoldingsAudit(
          apiPayload.AccountUniqueID || '',
          ssnorTIN,
          `User added new third-party holding: ${apiPayload.SecurityDescription} for ${apiPayload.YearMonth}`
        ).catch((auditError) => {
          console.log('[ThirdPartyHoldings] add audit error:', auditError);
        });
        setIsAddModalVisible(false);
        setIsAddSuccessModalVisible(true);
      })
      .catch((error) => {
        console.log('[ThirdPartyHoldings] insert error:', error);
        setAddSubmitError('Failed to add holding. Please try again.');
      })
      .finally(() => {
        setSubmittingAdd(false);
      });
  };

  const handleAddSuccessOk = () => {
    setIsAddSuccessModalVisible(false);
    setAddSubmitError(null);
    fetchHoldings();
  };

  const handleBeforeReview = (payload) => {
    return GetThirdPartyHoldings(ssnorTIN, null, payload.YearMonth, null)
      .then((response) => {
        const raw = response.data;
        const selectedAccountUniqueID =
          payload.AccountUniqueID ?? payload.LinkedPfmamAccount ?? null;
        const records = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.Table)
          ? raw.Table
          : Array.isArray(raw?.value)
          ? raw.value
          : Array.isArray(raw?.data)
          ? raw.data
          : [];
        const duplicate = records.some(
          (r) =>
            String(r.SecurityDescription || '').toLowerCase() === String(payload.SecurityDescription || '').toLowerCase() &&
            String(r.YearMonth) === String(payload.YearMonth) &&
            String(r.AccountUniqueID ?? r.AccountUniqueId ?? '') === String(selectedAccountUniqueID ?? '') &&
            Boolean(r.MonthlyStatement) === Boolean(payload.IncludeInMonthlyStatement)
        );
        if (duplicate) {
          setIsDuplicateModalVisible(true);
        }
        return duplicate;
      })
      .catch(() => false);
  };

  const handleDownload = () => {
    const submittedRecords = rawHoldings.map((record) => ({
      RecordSourceTab: 'Submitted',
      ...record,
    }));
    const inProgressRecords = rawOutstandingHoldings.map((record) => ({
      RecordSourceTab: 'In Progress',
      ...record,
    }));
    const downloadRecords = [...submittedRecords, ...inProgressRecords];

    if (downloadRecords.length === 0) return;

    const headers = Array.from(
      downloadRecords.reduce((acc, record) => {
        Object.keys(record).forEach((key) => acc.add(key));
        return acc;
      }, new Set())
    );
    const rows = downloadRecords.map((record) => headers.map((key) => record[key]));
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'third-party-holdings.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleHistory = () => {
    setIsHistoryModalVisible(true);
    setLoadingHistory(true);
    GetThirdPartyHoldingsAudit(ssnorTIN)
      .then((response) => {
        const raw = response.data;
        const records = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.Table)
          ? raw.Table
          : Array.isArray(raw?.value)
          ? raw.value
          : [];
        setAuditHistory(records);
      })
      .catch((error) => {
        console.log('[ThirdPartyHoldings] audit history error:', error);
      })
      .finally(() => {
        setLoadingHistory(false);
      });
  };

  const handleHistoryClose = () => {
    setIsHistoryModalVisible(false);
    setAuditHistory([]);
    setAuditSearchText('');
  };

  return (
    <AppLayout>
      <DropdownStyle />
      <Banner bgcolor={primary_color} height="124px">
        <Box
          display="flex"
          alignitems="center"
          height="100%"
          minwidth="100%"
          width="100%"
          zindex="3"
          position="relative"
          padding="44px 0 36px 0"
        >
          <Container>
            <Box
              display="flex"
              justifycontent="space-between"
              alignitems="flex-start"
              margin="0"
            >
              <Box flex="1">
                <Text
                  color={`${theme.colors.white}`}
                  fontSize="36px"
                  lineheight="44px"
                  label="Third-Party Holdings"
                />
              </Box>
            </Box>
          </Container>
        </Box>
      </Banner>
      <ContentWrapper rgbaHover={rgbaPrimary}>
        <Container>
          <Wrapper>
            {/* ── Period selector ── */}
            <PeriodBar>
              <PeriodLabel>Period:</PeriodLabel>
              <Select
                value={selectedMonth}
                onChange={(val) => setSelectedMonth(val)}
                style={{ width: 80 }}
                getPopupContainer={() => document.body}
                dropdownStyle={{ zIndex: 1060 }}
              >
                {MONTHS.map(({ value, label }) => (
                  <Option key={value} value={value}>{label}</Option>
                ))}
              </Select>
              <Select
                value={selectedYear}
                onChange={(val) => setSelectedYear(val)}
                style={{ width: 90 }}
                getPopupContainer={() => document.body}
                dropdownStyle={{ zIndex: 1060 }}
              >
                {YEARS.map((y) => <Option key={y} value={y}>{y}</Option>)}
              </Select>
              <span style={{ marginLeft: '8px', color: theme.colors.grey_tertiary, fontSize: theme.font_size.size_l, fontFamily: 'Inter', display: 'inline-flex', alignItems: 'center' }}>
                Showing submission status for period {periodLabel}
                <Tooltip
                  placement="top"
                  title="This page shows 3 months of data: the selected month and the 2 previous months."
                  overlayClassName="tooltip"
                >
                  <InfoCircleOutlined style={{ marginLeft: '8px', color: `${theme.colors.blue_primay}`, fontSize: '16px', cursor: 'pointer' }} />
                </Tooltip>
              </span>
            </PeriodBar>

            {/* ── Summary stat strip ── */}
            <StatStrip>
              <StatCard>
                <StatCardHeader>
                  <TeamOutlined style={{ fontSize: '18px', color: primary_color }} />
                  <Text label="Submission Progress" fontSize={`${theme.font_size.size_l}`} color={`${theme.colors.grey_tertiary}`} lineheight="20px" />
                </StatCardHeader>
                <StatValue color={primary_color}>{totalPeriods} / {totalTracked}</StatValue>
                <StatSubLabel>Holdings Submitted</StatSubLabel>
                <Progress
                  percent={completionPct}
                  strokeColor={primary_color}
                  trailColor={theme.colors.light_grey}
                  showInfo
                  size="small"
                  style={{ margin: '4px 0 0 0' }}
                />
              </StatCard>

              <StatCard>
                <StatCardHeader>
                  <DollarOutlined style={{ fontSize: '18px', color: '#1e7e34' }} />
                  <Text label="Total Balance Submitted" fontSize={`${theme.font_size.size_l}`} color={`${theme.colors.grey_tertiary}`} lineheight="20px" />
                </StatCardHeader>
                <StatValue color="#1e7e34">
                  {periodTotalBalance > 0
                    ? `$${periodTotalBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : '—'}
                </StatValue>
                <StatSubLabel>across {periodHoldings.length} holding{periodHoldings.length !== 1 ? 's' : ''} for {periodLabel}</StatSubLabel>
              </StatCard>

              <StatCard>
                <StatCardHeader>
                  <ClockCircleFilled style={{ fontSize: '18px', color: '#856404' }} />
                  <Text label="Outstanding Holdings" fontSize={`${theme.font_size.size_l}`} color={`${theme.colors.grey_tertiary}`} lineheight="20px" />
                </StatCardHeader>
                <StatValue color="#856404">
                  {outstandingData.length > 0 ? outstandingData.length : '—'}
                </StatValue>
              </StatCard>
            </StatStrip>
            <TableComponent
              className="table-primary"
              title={
                <>
                  <Box
                    display="flex"
                    justifycontent="space-between"
                    alignitems="center"
                    padding="16px 48px 16px 32px"
                    border={`1px solid ${theme.colors.grey_fossil}`}
                    borderwidth="0 0 1px 0"
                  >
                    <InputComponent
                      type="search"
                      placeholder="Search holdings..."
                      maxLength={100}
                      value={searchText}
                      onInput={(e) => {
                        setSearchText(e.target.value);
                        setPagination((prev) => ({ ...prev, current: 1 }));
                      }}
                      onSearch={(value) => {
                        setSearchText(value);
                        setPagination((prev) => ({ ...prev, current: 1 }));
                      }}
                    />
                    <Box display="flex" alignitems="center">
                      {(webuserAccessType === 1 || webuserAccessType === 2) && (
                        <ButtonAnt
                          type="link"
                          label="All Clients"
                          icon={<TeamOutlined />}
                          onClick={() => history.push('/all-third-party-holdings')}
                          style={{ marginRight: '32px' }}
                        />
                      )}
                      <ButtonAnt
                        type="link"
                        label="Download"
                        icon={<DownloadOutlined />}
                        onClick={handleDownload}
                        style={{ marginRight: '32px' }}
                      />
                      <ButtonAnt
                        type="link"
                        label="History"
                        icon={<HistoryOutlined />}
                        onClick={handleHistory}
                        style={{ marginRight: '32px' }}
                      />
                      {canShowAddNewButton && (
                        <ButtonAnt
                          type="link"
                          label="Add New"
                          icon={<PlusCircleOutlined />}
                          onClick={handleAddNew}
                        />
                      )}
                    </Box>
                  </Box>
                  <Box
                    display="flex"
                    alignitems="center"
                    padding="10px 32px"
                    border={`1px solid ${theme.colors.grey_fossil}`}
                    borderwidth="0 0 1px 0"
                  >
                    <TabToggleGroup>
                      <TabToggleBtn
                        active={activeTab === 'submitted'}
                        onClick={() => { setActiveTab('submitted'); setPagination((p) => ({ ...p, current: 1 })); }}
                      >
                        <CheckCircleOutlined style={{ fontSize: '12px', color: activeTab === 'submitted' ? '#1e7e34' : theme.colors.grey_tertiary }} />
                        Submitted
                        <Tag
                          color={activeTab === 'submitted' ? 'green' : 'default'}
                          style={{ marginLeft: 2, borderRadius: 20, fontSize: 11, lineHeight: '16px', padding: '0 6px' }}
                        >
                          {periodHoldings.length}
                        </Tag>
                      </TabToggleBtn>
                      <TabToggleBtn
                        active={activeTab === 'outstanding'}
                        onClick={() => { setActiveTab('outstanding'); setPagination((p) => ({ ...p, current: 1 })); fetchOutstandingHoldings(); }}
                      >
                        <ClockCircleFilled style={{ fontSize: '12px', color: activeTab === 'outstanding' ? '#856404' : theme.colors.grey_tertiary }} />
                        In Progress
                        <Tag
                          color={activeTab === 'outstanding' ? 'warning' : 'default'}
                          style={{ marginLeft: 2, borderRadius: 20, fontSize: 11, lineHeight: '16px', padding: '0 6px' }}
                        >
                          {outstandingData.length}
                        </Tag>
                      </TabToggleBtn>
                    </TabToggleGroup>
                  </Box>
                </>
              }
              columns={activeTab === 'outstanding' ? [
                {
                  title: 'Security Description',
                  dataIndex: 'securityDescription',
                  key: 'securityDescription',
                  className: 'sort-space-none',
                  render: (text) => (
                    <Text label={text} fontSize={`${theme.font_size.size_xl}`} lineheight="20px" />
                  ),
                },
                {
                  title: 'Portfolio',
                  dataIndex: 'portfolio',
                  key: 'portfolio',
                  className: 'sort-space-none',
                  render: (text) => (
                    <Text label={text} fontSize={`${theme.font_size.size_xl}`} lineheight="20px" />
                  ),
                },
              ] : columns}
              loading={activeTab === 'outstanding' ? loadingOutstanding : loadingHoldings}
              dataSource={activeTab === 'outstanding' ? outstandingData : filteredData}
              locale={{
                emptyText: <NoDataFound text="No third-party holdings" />,
              }}
              showSorterTooltip={false}
              onChange={(e) => handleTableChange(e.current)}
              pagination={pagination}
              rowClassName={(record) => (!record.isActive ? 'inactive-row' : '')}
            />
          </Wrapper>
        </Container>
      </ContentWrapper>
      {isDetailsModalVisible && selectedRecord && !isReviewModalVisible && (
        <Modal
          title="Additional Details"
          content={
            loadingDetails ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
                <span>Loading...</span>
              </div>
            ) : (
              <>
                <ThirdPartyHoldingsDetailsModal
                  formValues={formValues}
                  editingField={editingField}
                  setEditingField={setEditingField}
                  onFieldChange={handleFieldChange}
                  ratingOptions={ratingOptions}
                  securityTypeOptions={securityTypeOptions}
                  fieldErrors={detailsFormErrors}
                />
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '32px' }}>
                  {canShowAddNewButton && (
                    <HoverButton
                      type="primary"
                      linkTextColor={primary_color}
                      label="Update Details"
                      disabled={getPendingChanges().length === 0}
                      onClick={handleUpdateDetailsClick}
                    />
                  )}
                </div>
              </>
            )
          }
          visible={isDetailsModalVisible}
          onCancel={handleDetailsModalClose}
          footer={null}
          width={590}
          bodyStyle={{
            padding: '32px 32px 48px 32px',
          }}
          maskStyle={{
            backgroundColor: `${theme.rgba.modal}`,
            zIndex: '1051',
          }}
          centered={true}
        />
      )}
      {isReviewModalVisible && (
        <Modal
          title="Review Changes"
          content={
            <ThirdPartyHoldingsReviewModal
              changes={getPendingChanges()}
              onConfirm={handleConfirmSubmit}
              onBack={handleReviewBack}
              primaryColor={primary_color}
              submitting={submittingUpdate}
              submitError={submitError}
            />
          }
          visible={isReviewModalVisible}
          onCancel={handleDetailsModalClose}
          footer={null}
          width={590}
          bodyStyle={{
            padding: '32px 32px 48px 32px',
          }}
          maskStyle={{
            backgroundColor: `${theme.rgba.modal}`,
            zIndex: '1051',
          }}
          centered={true}
        />
      )}
      {isHistoryModalVisible && (
        <Modal
          title="Audit History"
          content={
            loadingHistory ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
                <span>Loading...</span>
              </div>
            ) : (
              <>
                <Box padding="0 0 16px 0">
                  <InputComponent
                    type="search"
                    placeholder="Search history..."
                    maxLength={100}
                    value={auditSearchText}
                    onInput={(e) => setAuditSearchText(e.target.value)}
                    onSearch={(value) => setAuditSearchText(value)}
                  />
                </Box>
                <ModalTableWrapper>
                <TableComponent
                  className="table-primary"
                  columns={[
                    {
                      title: 'User',
                      dataIndex: 'WebUserKey',
                      key: 'WebUserKey',
                      width: 140,
                      className: 'sort-space-none',
                      sorter: (a, b) => (a.WebUserKey || '').localeCompare(b.WebUserKey || ''),
                      render: (text) => (
                        <Text label={text} fontSize={`${theme.font_size.size_xl}`} lineheight="20px" />
                      ),
                    },
                    {
                      title: 'Account',
                      dataIndex: 'AccountUniqueID',
                      key: 'AccountUniqueID',
                      width: 140,
                      className: 'sort-space-none',
                      sorter: (a, b) => (a.AccountUniqueID || '').localeCompare(b.AccountUniqueID || ''),
                      render: (text) => (
                        <Text label={text} fontSize={`${theme.font_size.size_xl}`} lineheight="20px" />
                      ),
                    },
                    {
                      title: 'Action',
                      dataIndex: 'UserAction',
                      key: 'UserAction',
                      className: 'sort-space-none',
                      sorter: (a, b) => (a.UserAction || '').localeCompare(b.UserAction || ''),
                      render: (text) => (
                        <Text label={text} fontSize={`${theme.font_size.size_xl}`} lineheight="20px" />
                      ),
                    },
                    {
                      title: 'Date & Time',
                      dataIndex: 'ActionDateTime',
                      key: 'ActionDateTime',
                      width: 170,
                      className: 'sort-space-none',
                      sorter: (a, b) => new Date(a.ActionDateTime) - new Date(b.ActionDateTime),
                      defaultSortOrder: 'descend',
                      render: (text) => (
                        <Text
                          label={text ? new Date(text).toLocaleString('en-US') : '—'}
                          fontSize={`${theme.font_size.size_xl}`}
                          lineheight="20px"
                        />
                      ),
                    },
                  ]}
                  dataSource={auditHistory
                    .filter((row) =>
                      !auditSearchText ||
                      ['WebUserKey', 'AccountUniqueID', 'UserAction'].some(
                        (field) =>
                          row[field] &&
                          row[field].toString().toLowerCase().includes(auditSearchText.toLowerCase())
                      )
                    )
                    .map((row, i) => ({ ...row, key: i }))}
                  locale={{ emptyText: <NoDataFound text="No audit history found" /> }}
                  showSorterTooltip={false}
                  pagination={{ pageSize: 10, size: 'default', position: ['bottomRight'] }}
                />
                </ModalTableWrapper>
              </>
            )
          }
          visible={isHistoryModalVisible}
          onCancel={handleHistoryClose}
          footer={null}
          width={1100}
          bodyStyle={{ padding: '24px 32px 40px 32px' }}
          maskStyle={{
            backgroundColor: `${theme.rgba.modal}`,
            zIndex: '1051',
          }}
          centered={true}
        />
      )}
      {isSuccessModalVisible && (
        <Modal
          title="Update Successful"
          content={
            <div style={{ textAlign: 'center', padding: '16px 0 8px 0' }}>
              <CheckCircleOutlined
                style={{ fontSize: '48px', color: theme.colors.pie_green, marginBottom: '16px' }}
              />
              <Text
                label="The account details have been successfully updated!"
                color={`${theme.colors.black_secondary}`}
                fontSize={`${theme.font_size.size_xl}`}
                lineheight="24px"
                padding="0 0 32px 0"
              />
              <HoverButton
                type="primary"
                linkTextColor={primary_color}
                label="Ok"
                onClick={handleSuccessOk}
                style={{ minWidth: '120px' }}
              />
            </div>
          }
          visible={isSuccessModalVisible}
          onCancel={handleSuccessOk}
          footer={null}
          width={420}
          bodyStyle={{ padding: '32px 32px 48px 32px' }}
          maskStyle={{
            backgroundColor: `${theme.rgba.modal}`,
            zIndex: '1051',
          }}
          centered={true}
        />
      )}
      {isRemoveModalVisible && recordToRemove && (
        <Modal
          title="Remove Holding?"
          content={
            <div style={{ padding: '8px 0 24px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                <WarningOutlined style={{ fontSize: '22px', color: theme.colors.danger_red }} />
              </div>
              <p style={{ fontSize: theme.font_size.size_xl, lineHeight: '24px', color: theme.colors.grey_tertiary, margin: '0 0 16px 0' }}>
                Upon confirmation, this third-party holding will be permanently removed. It will no longer be available for updates and will not be included in future reports.
              </p>
              <p style={{ fontSize: theme.font_size.size_xl, lineHeight: '24px', color: theme.colors.grey_tertiary, margin: 0 }}>
                Select Confirm to proceed with the removal or Cancel to keep the holding.
              </p>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '32px' }}>
                <ButtonAnt
                  type="secondary"
                  label="Cancel"
                  onClick={handleCancelRemove}
                />
                <ButtonAnt
                  type="primary"
                  label="Confirm"
                  onClick={handleConfirmRemove}
                  style={{ backgroundColor: theme.colors.danger_red, borderColor: theme.colors.danger_red }}
                />
              </div>
            </div>
          }
          visible={isRemoveModalVisible}
          onCancel={handleCancelRemove}
          footer={null}
          width={480}
          bodyStyle={{
            padding: '32px 32px 48px 32px',
          }}
          maskStyle={{
            backgroundColor: `${theme.rgba.modal}`,
            zIndex: '1051',
          }}
          centered={true}
        />
      )}
      {isAddModalVisible && (
        <Modal
          title="Add Third Party Holding"
          content={
            <ThirdPartyHoldingsAddModal
              onSubmit={handleAddSubmit}
              onCancel={handleAddModalClose}
              primaryColor={primary_color}
              submitting={submittingAdd}
              submitError={addSubmitError}
              linkedAccounts={[...new Set(thirdPartyHoldings.map((h) => h.linkedAccount).filter(Boolean))]}
              onBeforeReview={handleBeforeReview}
              securityDescriptionOptions={securityDescriptionOptions}
              securityTypeOptions={securityTypeOptions}
              portfolioOptions={portfolioOptions}
              ratingOptions={ratingOptions}
            />
          }
          visible={isAddModalVisible}
          onCancel={handleAddModalClose}
          footer={null}
          width={620}
          bodyStyle={{
            padding: '32px 32px 48px 32px',
            maxHeight: '80vh',
            overflowY: 'auto',
          }}
          maskStyle={{
            backgroundColor: `${theme.rgba.modal}`,
            zIndex: '1051',
          }}
          centered={true}
        />
      )}
      {isDuplicateModalVisible && (
        <Modal
          title="Duplicate Holding Found"
          content={
            <div style={{ padding: '8px 0 24px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                <WarningOutlined style={{ fontSize: '22px', color: theme.colors.danger_red }} />
              </div>
              <p style={{ fontSize: theme.font_size.size_xl, lineHeight: '24px', color: theme.colors.grey_tertiary, margin: '0 0 16px 0' }}>
                The holding you are submitting may already exist for the selected time period.
              </p>
              <p style={{ fontSize: theme.font_size.size_xl, lineHeight: '24px', color: theme.colors.grey_tertiary, margin: 0 }}>
                Please review the existing holding or change the details before submitting.
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '32px' }}>
                <HoverButton
                  type="primary"
                  linkTextColor={primary_color}
                  label="Ok"
                  onClick={() => setIsDuplicateModalVisible(false)}
                  style={{ minWidth: '120px' }}
                />
              </div>
            </div>
          }
          visible={isDuplicateModalVisible}
          onCancel={() => setIsDuplicateModalVisible(false)}
          footer={null}
          width={480}
          bodyStyle={{ padding: '32px 32px 48px 32px' }}
          maskStyle={{
            backgroundColor: `${theme.rgba.modal}`,
            zIndex: '1052',
          }}
          centered={true}
        />
      )}
      {isAddSuccessModalVisible && (
        <Modal
          title="Holding Added"
          content={
            <div style={{ textAlign: 'center', padding: '16px 0 8px 0' }}>
              <CheckCircleOutlined
                style={{ fontSize: '48px', color: theme.colors.pie_green, marginBottom: '16px' }}
              />
              <Text
                label="The new holding has been successfully added!"
                color={`${theme.colors.black_secondary}`}
                fontSize={`${theme.font_size.size_xl}`}
                lineheight="24px"
                padding="0 0 32px 0"
              />
              <HoverButton
                type="primary"
                linkTextColor={primary_color}
                label="Ok"
                onClick={handleAddSuccessOk}
                style={{ minWidth: '120px' }}
              />
            </div>
          }
          visible={isAddSuccessModalVisible}
          onCancel={handleAddSuccessOk}
          footer={null}
          width={420}
          bodyStyle={{ padding: '32px 32px 48px 32px' }}
          maskStyle={{
            backgroundColor: `${theme.rgba.modal}`,
            zIndex: '1051',
          }}
          centered={true}
        />
      )}
    </AppLayout>
  );
};

export default ThirdPartyHoldingsContainer;
